import { dist2, iatan2, idiv, imax, isqrt, mulBp, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, Hero } from '../types.ts';
import { damageHero, ev, spawnEnemy } from './combat.ts';
import { stepToward } from './enemies.ts';

/**
 * The Drowned Dragon (La Orilla 5, docs/SAGAS.md): a nightmare got into the real dragon's bones and raised them. Its
 * rule: put it in the lane when the fin comes down. The fin's wave (battle/fin.ts) takes a third of its life if it is
 * in the lane and can't move: stunned, taunted, held by roots or Yorra's harpoon, drawing breath, or going after the
 * dragon's heart. Otherwise the wave goes past it. Blows land on it at a share. Every while it draws breath, still,
 * and breathes fire down a band (steam, with its heart gone to the forge). Yorra's boats harpoon it from the sea.
 *
 * The heart, if the group took it, is carried by one hero in both of the chapter's fights: slower, and the dragon
 * (and its fire, in the first) goes after whoever has it. Dropped where its carrier falls, lifted by whoever comes.
 */

const dragon = (s: BattleState): Enemy | undefined => (s.sunken?.id ? s.enemies.find((e) => e.id === s.sunken?.id && e.hp > 0) : undefined);

/** Whether a point is in the band of its breath, from the dragon `d` towards `aim`. */
export const inBreath = (d: { x: number; y: number }, aim: { x: number; y: number }, br: { half: number; range: number }, x: number, y: number): boolean => {
  const len = imax(1, isqrt(dist2(d.x, d.y, aim.x, aim.y)));
  const ux = idiv((aim.x - d.x) * 1000, len);
  const uy = idiv((aim.y - d.y) * 1000, len);
  const along = idiv((x - d.x) * ux + (y - d.y) * uy, 1000);
  const across = idiv(-(x - d.x) * uy + (y - d.y) * ux, 1000);
  return along >= 0 && along <= br.range && across >= -br.half && across <= br.half;
};

const state = (s: BattleState) => (s.sunken ??= { id: 0, draw: 0, aim: { x: 0, y: 0 }, held: 0, boat: 0, done: 0 });

/** Whoever carries the heart (standing), if anyone. */
export const heartCarrier = (s: BattleState): Hero | undefined => {
  const h = s.sunken?.heart;
  if (!h || h.at !== 'carried') return undefined;
  return s.heroes.find((x) => x.pid === h.pid && x.downTicks === 0);
};

export const sunkenTick = (s: BattleState): void => {
  const r = s.rules?.sunken;
  if (!r) return;
  const st = state(s);
  if (!s.started || st.done) return;
  // The heart: in the hands of the first hero to begin with; dropped where its carrier falls; lifted by whoever comes.
  const hk = r.heart;
  if (hk) {
    if (!st.heart) st.heart = { at: 'carried', pid: s.heroes.slice().sort((a, b) => a.id - b.id)[0]?.pid ?? '', x: 0, y: 0 };
    const heart = st.heart;
    const by = heartCarrier(s);
    if (heart.at === 'carried' && !by) {
      heart.at = 'ground';
      heart.pid = '';
      ev(s, { t: 'sunken', kind: 'drop', x: heart.x, y: heart.y, tx: 0, ty: 0 });
    } else if (by) {
      heart.x = by.x;
      heart.y = by.y;
    }
    if (heart.at === 'ground') {
      const lift = s.heroes.find((x) => x.downTicks === 0 && withinRange(x.x, x.y, heart.x, heart.y, hk.reach));
      if (lift) {
        heart.at = 'carried';
        heart.pid = lift.pid;
        ev(s, { t: 'sunken', kind: 'lift', x: heart.x, y: heart.y, tx: 0, ty: 0 });
      }
    }
  }
  const b = r.boss;
  if (!b) return;
  if (!st.id) {
    if (s.waveT < b.rise) return;
    const e = spawnEnemy(s, 'sunken', 1, false, 0, b.at.x, b.at.y, b.hp);
    e.facing = iatan2(-b.at.y, -b.at.x);
    st.id = e.id;
    ev(s, { t: 'sunken', kind: 'rise', x: b.at.x, y: b.at.y, tx: 0, ty: 0 });
  }
  const d = dragon(s);
  if (!d) {
    st.done = s.tick;
    const at = s.enemies.find((e) => e.id === st.id) ?? b.at;
    ev(s, { t: 'sunken', kind: 'felled', x: at.x, y: at.y, tx: 0, ty: 0 });
    return;
  }
  // Yorra's boats, one after another, put a harpoon in it from the sea.
  if (b.boats.length && s.waveT >= b.harpoon.first && (s.waveT - b.harpoon.first) % b.harpoon.every === 0) {
    const boat = b.boats[st.boat % b.boats.length] as { x: number; y: number };
    st.boat++;
    st.held = s.tick + b.harpoon.hold;
    ev(s, { t: 'sunken', kind: 'harpoon', x: boat.x, y: boat.y, tx: d.x, ty: d.y });
  }
  // Its breath: drawn still for a moment, then down a band towards where it aimed.
  if (st.draw) {
    if (s.tick < st.draw) return;
    st.draw = 0;
    d.anim = 'attack';
    d.animT = 0;
    const len = imax(1, isqrt(dist2(d.x, d.y, st.aim.x, st.aim.y)));
    const ux = idiv((st.aim.x - d.x) * 1000, len);
    const uy = idiv((st.aim.y - d.y) * 1000, len);
    for (const h of s.heroes) {
      if (h.downTicks > 0 || !inBreath(d, st.aim, b.breath, h.x, h.y)) continue;
      damageHero(s, h, imax(1, mulBp(h.stats.maxHp, b.breath.hitBp)), d);
    }
    ev(s, { t: 'sunken', kind: 'breath', x: d.x, y: d.y, tx: d.x + idiv(ux * b.breath.range, 1000), ty: d.y + idiv(uy * b.breath.range, 1000) });
    return;
  }
  if (d.stun > 0 || s.waveT < b.rise + b.breath.every || (s.waveT - b.rise) % b.breath.every !== 0) return;
  const t = target(s, d);
  if (!t) return;
  st.draw = s.tick + b.breath.draw;
  st.aim = { x: t.x, y: t.y };
  d.facing = iatan2(t.y - d.y, t.x - d.x);
  ev(s, { t: 'sunken', kind: 'draw', x: d.x, y: d.y, tx: t.x, ty: t.y });
};

/** Who it goes after: whoever taunted it, else whoever has its heart, else the nearest hero standing. */
const target = (s: BattleState, e: Enemy): Hero | undefined => {
  if (e.taunter > 0) {
    const h = s.heroes.find((x) => x.id === e.taunter && x.downTicks === 0);
    if (h) return h;
  }
  const c = heartCarrier(s);
  if (c) return c;
  let best: Hero | undefined;
  let bd = 0x7fffffff;
  for (const h of s.heroes) {
    if (h.downTicks > 0) continue;
    const d = dist2(e.x, e.y, h.x, h.y);
    if (d < bd) {
      bd = d;
      best = h;
    }
  }
  return best;
};

/** Its walk and its claws (from battle/enemies.ts, after a stun has had its say): still while held or drawing breath. */
export const sunkenMove = (s: BattleState, e: Enemy, px: number, attack: (h: Hero) => void): void => {
  const st = s.sunken;
  if (st && (st.draw || st.held > s.tick)) {
    e.anim = st.draw ? 'cast' : 'idle';
    return;
  }
  const h = target(s, e);
  if (!h) {
    e.anim = 'idle';
    return;
  }
  e.tgtKind = 1;
  e.tgtId = h.id;
  if (withinRange(e.x, e.y, h.x, h.y, 1650)) attack(h);
  else {
    e.anim = 'walk';
    stepToward(e, h.x, h.y, px);
  }
};

/**
 * Whether it can't get out of the fin's way: stunned, taunted, held (roots or the harpoon), drawing breath, or going
 * after its heart.
 */
export const sunkenPinned = (s: BattleState, e: Enemy): boolean => {
  const st = s.sunken;
  return e.stun > 0 || e.tauntTicks > 0 || (e.grounded ?? 0) > 0 || !!st?.draw || (st?.held ?? 0) > s.tick || !!heartCarrier(s);
};

/** The fin come down on it in its lane: crushed if it couldn't move, untouched if it could. */
export const sunkenWave = (s: BattleState, inLane: (x: number, y: number) => boolean): void => {
  const b = s.rules?.sunken?.boss;
  const d = dragon(s);
  if (!b || !d || !inLane(d.x, d.y)) return;
  if (!sunkenPinned(s, d)) {
    ev(s, { t: 'sunken', kind: 'shrug', x: d.x, y: d.y, tx: 0, ty: 0 });
    return;
  }
  d.hp = imax(0, d.hp - mulBp(d.maxHp, b.crushBp));
  d.lastHit = s.tick;
  ev(s, { t: 'sunken', kind: 'crush', x: d.x, y: d.y, tx: 0, ty: 0 });
};

/** What a blow on it lands as. */
export const sunkenTakenBp = (s: BattleState): number => s.rules?.sunken?.boss?.takenBp ?? 10000;

/** The heart's carrier's pace. */
export const sunkenPaceBp = (s: BattleState, h: Hero): number => {
  const hk = s.rules?.sunken?.heart;
  return hk && s.sunken?.heart?.at === 'carried' && s.sunken.heart.pid === h.pid ? hk.carryBp : 10000;
};

/** The dragon felled: won (a moment later); the stilt-houses fallen: lost. */
export const sunkenResult = (s: BattleState): 'won' | 'lost' | null => {
  const b = s.rules?.sunken?.boss;
  if (!b) return null;
  if (s.keep.hp <= 0) return 'lost';
  return s.sunken?.done && s.tick - s.sunken.done >= b.beat ? 'won' : null;
};
